// src/components/OrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../services/api';

const OrdersPage = ({ auth, currency, convertPrice, onBack }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check mobile screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch user orders
  useEffect(() => {
    if (auth) {
      fetchOrders();
    }
  }, [auth]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ordersAPI.getMyOrders();
      
      if (response.success) {
        setOrders(response.orders || []);
      } else {
        setError('Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return '#f59e0b';
      case 'confirmed': 
      case 'processing': return '#3b82f6';
      case 'shipped': return '#8b5cf6';
      case 'delivered': return '#10b981';
      case 'cancelled': 
      case 'refunded': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'Pending';
      case 'confirmed': 
      case 'processing': return 'Processing';
      case 'shipped': return 'Shipped';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      case 'refunded': return 'Refunded';
      default: return status || 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const response = await ordersAPI.cancel(orderId);
      if (response.success) {
        alert('Order cancelled successfully');
        fetchOrders(); // Refresh orders list
      }
    } catch (error) {
      alert(`Failed to cancel order: ${error.message}`);
    }
  };

  const handleTrackOrder = async (orderId) => {
    try {
      const response = await ordersAPI.track(orderId);
      if (response.success) {
        alert(`Tracking Info: ${JSON.stringify(response.tracking, null, 2)}`);
      }
    } catch (error) {
      alert(`Failed to get tracking info: ${error.message}`);
    }
  };

  if (!auth) {
    return (
      <div className="orders-container" style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '2rem',
        textAlign: 'center'
      }}>
        <style jsx>{`
          .orders-container {
            padding: 1rem;
          }
          
          @media (max-width: 768px) {
            .orders-container {
              padding: 0.5rem;
            }
          }
        `}</style>
        <div style={{ 
          background: 'white', 
          padding: '3rem', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ color: '#e63946', marginBottom: '1rem' }}>Please Login</h2>
          <p style={{ color: '#64748b', marginBottom: '2rem' }}>
            You need to be logged in to view your orders.
          </p>
          <button 
            onClick={onBack}
            style={{
              background: '#e63946',
              color: 'white',
              border: 'none',
              padding: '0.8rem 2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orders-container" style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '2rem',
        textAlign: 'center'
      }}>
        <style jsx>{`
          .orders-container {
            padding: 1rem;
          }
          
          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 5px solid #f3f3f3;
            border-top: 5px solid #e63946;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 2rem auto;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{ 
          background: 'white', 
          padding: '3rem', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div className="loading-spinner"></div>
          <p style={{ color: '#64748b', marginTop: '1rem' }}>Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-container" style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '2rem'
      }}>
        <style jsx>{`
          .orders-container {
            padding: 1rem;
          }
        `}</style>
        <div style={{ 
          background: 'white', 
          padding: '3rem', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#ef4444' }}>⚠️</div>
          <h3 style={{ color: '#334155', marginBottom: '1rem' }}>
            Error Loading Orders
          </h3>
          <p style={{ color: '#64748b', marginBottom: '2rem' }}>
            {error}
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button 
              onClick={fetchOrders}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Try Again
            </button>
            <button 
              onClick={onBack}
              style={{
                background: '#e63946',
                color: 'white',
                border: 'none',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <style jsx>{`
        .orders-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem;
        }

        @media (max-width: 768px) {
          .orders-container {
            padding: 0.5rem;
          }
        }

        .orders-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        @media (max-width: 768px) {
          .orders-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
        }

        .back-button {
          background: none;
          border: 1px solid #e2e8f0;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          color: #64748b;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }

        .back-button:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .orders-title {
          color: #1e293b;
          font-size: 2rem;
          font-weight: 700;
          margin: 0;
        }

        @media (max-width: 768px) {
          .orders-title {
            font-size: 1.5rem;
          }
        }

        .refresh-button {
          background: #10b981;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }

        .refresh-button:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .refresh-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .empty-orders {
          background: white;
          padding: 3rem;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        @media (max-width: 768px) {
          .empty-orders {
            padding: 2rem 1rem;
          }
        }

        .empty-orders-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .empty-orders-title {
          color: #334155;
          margin-bottom: 1rem;
          font-size: 1.5rem;
        }

        .empty-orders-text {
          color: #64748b;
          margin-bottom: 2rem;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
        }

        .orders-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .order-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .order-card:hover {
          box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .order-header {
          background: #f8fafc;
          padding: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        @media (max-width: 768px) {
          .order-header {
            padding: 1rem;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
        }

        .order-info {
          flex: 1;
          min-width: 0;
        }

        .order-number {
          color: #1e293b;
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .order-date {
          color: #64748b;
          font-size: 0.9rem;
        }

        .order-status-amount {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .order-status {
          background: #f59e0b;
          color: white;
          padding: 0.4rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: capitalize;
          white-space: nowrap;
        }

        .order-amount {
          color: #1e293b;
          font-weight: 700;
          font-size: 1.2rem;
          white-space: nowrap;
        }

        .order-content {
          padding: 1.5rem;
        }

        @media (max-width: 768px) {
          .order-content {
            padding: 1rem;
          }
        }

        .order-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .order-item {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
          align-items: center;
        }

        @media (max-width: 480px) {
          .order-item {
            flex-direction: column;
            text-align: center;
            padding: 0.75rem;
          }
        }

        .item-image {
          width: 60px;
          height: 80px;
          object-fit: cover;
          border-radius: 4px;
          flex-shrink: 0;
        }

        @media (max-width: 480px) {
          .item-image {
            width: 80px;
            height: 100px;
          }
        }

        .item-details {
          flex: 1;
          min-width: 0;
        }

        @media (max-width: 480px) {
          .item-details {
            width: 100%;
          }
        }

        .item-name {
          color: #1e293b;
          margin-bottom: 0.25rem;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        @media (max-width: 480px) {
          .item-name {
            white-space: normal;
            text-overflow: initial;
          }
        }

        .item-author {
          color: #64748b;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .item-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.9rem;
        }

        @media (max-width: 480px) {
          .item-meta {
            flex-direction: column;
            gap: 0.5rem;
          }
        }

        .item-quantity {
          color: #64748b;
        }

        .item-price {
          color: #e63946;
          font-weight: 700;
        }

        .order-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-top: 1.5rem;
        }

        @media (max-width: 768px) {
          .order-details {
            grid-template-columns: 1fr;
          }
        }

        .detail-section {
          background: #f1f5f9;
          padding: 1rem;
          border-radius: 8px;
        }

        .detail-title {
          color: #475569;
          font-size: 0.8rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-content {
          color: #334155;
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .shipping-address {
          white-space: pre-line;
        }

        .payment-method {
          text-transform: capitalize;
          font-weight: 500;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.25rem;
        }

        .summary-total {
          font-weight: 700;
          color: #e63946;
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid #cbd5e1;
        }

        .order-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.5rem;
          flex-wrap: wrap;
        }

        .action-button {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .action-button.track {
          background: #3b82f6;
          color: white;
        }

        .action-button.track:hover {
          background: #2563eb;
        }

        .action-button.cancel {
          background: #ef4444;
          color: white;
        }

        .action-button.cancel:hover {
          background: #dc2626;
        }

        .action-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .delivery-info {
          margin-top: 1rem;
          padding: 0.75rem;
          background: #dcfce7;
          border-radius: 6px;
          border: 1px solid #bbf7d0;
          color: #166534;
          font-size: 0.9rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .delivery-info svg {
          flex-shrink: 0;
        }

        /* Modal styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
        }

        /* Order count badge */
        .orders-count {
          background: #e63946;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }
      `}</style>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, color: '#1e293b' }}>
                Order Details - {selectedOrder.orderNumber || selectedOrder.id}
              </h2>
              <button className="modal-close" onClick={() => setSelectedOrder(null)}>
                ×
              </button>
            </div>
            <div className="order-content">
              {/* Render order details similar to the card but expanded */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem',
                padding: '1rem',
                background: '#f8fafc',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Order Date</div>
                  <div style={{ color: '#1e293b', fontWeight: '600' }}>
                    {formatDate(selectedOrder.createdAt || selectedOrder.orderDate)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Status</div>
                  <span style={{
                    background: getStatusColor(selectedOrder.status),
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    display: 'inline-block'
                  }}>
                    {getStatusText(selectedOrder.status)}
                  </span>
                </div>
              </div>
              
              {/* Add more detailed information as needed */}
              <button 
                onClick={() => setSelectedOrder(null)}
                style={{
                  background: '#e63946',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  width: '100%',
                  marginTop: '1rem'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="orders-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
          <h1 className="orders-title">
            My Orders
            {orders.length > 0 && <span className="orders-count">{orders.length}</span>}
          </h1>
        </div>
        
        <button 
          className="refresh-button"
          onClick={fetchOrders}
          disabled={loading}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 4v6h-6"></path>
            <path d="M1 20v-6h6"></path>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
            <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="empty-orders">
          <div className="empty-orders-icon">📦</div>
          <h3 className="empty-orders-title">No Orders Yet</h3>
          <p className="empty-orders-text">
            You haven't placed any orders yet. Start shopping to see your orders here!
          </p>
          <button 
            onClick={onBack}
            style={{
              background: '#e63946',
              color: 'white',
              border: 'none',
              padding: '0.8rem 2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Start Shopping
          </button>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order.id || order._id} className="order-card">
              <div className="order-header">
                <div className="order-info">
                  <div className="order-number">
                    Order #{order.orderNumber || order.id?.slice(-8) || 'N/A'}
                    {order.paymentMethod === 'razorpay' && order.paymentStatus === 'paid' && (
                      <span title="Paid via Razorpay" style={{ color: '#10b981' }}>
                        ✓
                      </span>
                    )}
                  </div>
                  <div className="order-date">
                    Placed on {formatDate(order.createdAt || order.orderDate || order.created_at)}
                  </div>
                </div>
                <div className="order-status-amount">
                  <span 
                    className="order-status"
                    style={{ background: getStatusColor(order.status) }}
                  >
                    {getStatusText(order.status)}
                  </span>
                  <span className="order-amount">
                    {convertPrice(order.total || order.amount || 0)}
                  </span>
                </div>
              </div>

              <div className="order-content">
                <div className="order-items">
                  {(order.items || order.products || []).map((item, index) => (
                    <div key={index} className="order-item">
                      <img 
                        src={item.image || item.product?.image || 'https://via.placeholder.com/60x80?text=Book'}
                        alt={item.name || item.product?.name}
                        className="item-image"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/60x80?text=Book';
                        }}
                      />
                      <div className="item-details">
                        <div className="item-name">
                          {item.name || item.product?.name || 'Unknown Product'}
                        </div>
                        <div className="item-author">
                          by {item.author || item.product?.author || 'Unknown Author'}
                        </div>
                        <div className="item-meta">
                          <span className="item-quantity">
                            Qty: {item.quantity || 1}
                          </span>
                          <span className="item-price">
                            {convertPrice((item.price || item.product?.price || 0) * (item.quantity || 1))}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-details">
                  <div className="detail-section">
                    <div className="detail-title">Shipping Address</div>
                    <div className="detail-content shipping-address">
                      {order.shippingInfo ? (
                        <>
                          <strong>{order.shippingInfo.fullName || order.shippingInfo.name}</strong><br />
                          {order.shippingInfo.address}<br />
                          {order.shippingInfo.city}, {order.shippingInfo.state}<br />
                          {order.shippingInfo.postalCode || order.shippingInfo.zipCode}<br />
                          {order.shippingInfo.country || 'India'}
                        </>
                      ) : order.shippingAddress ? (
                        <>
                          <strong>{order.shippingAddress.name}</strong><br />
                          {order.shippingAddress.street}<br />
                          {order.shippingAddress.city}, {order.shippingAddress.state}<br />
                          {order.shippingAddress.pincode}<br />
                          India
                        </>
                      ) : (
                        'Address not available'
                      )}
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="detail-title">Payment Method</div>
                    <div className="detail-content payment-method">
                      {order.paymentMethod === 'cod' && 'Cash on Delivery'}
                      {order.paymentMethod === 'razorpay' && 'Razorpay (Online)'}
                      {order.paymentMethod === 'credit_card' && 'Credit Card'}
                      {order.paymentMethod === 'upi' && 'UPI'}
                      {!['cod', 'razorpay', 'credit_card', 'upi'].includes(order.paymentMethod) && 
                        (order.paymentMethod || 'Not specified')}
                      {order.paymentStatus && (
                        <div style={{ 
                          fontSize: '0.8rem', 
                          color: order.paymentStatus === 'paid' ? '#10b981' : '#f59e0b',
                          marginTop: '0.25rem'
                        }}>
                          Status: {order.paymentStatus}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="detail-title">Order Summary</div>
                    <div className="detail-content">
                      <div className="summary-row">
                        <span>Subtotal:</span>
                        <span>{convertPrice(order.subtotal || order.items?.reduce((sum, item) => 
                          sum + (item.price || 0) * (item.quantity || 1), 0) || 0)}</span>
                      </div>
                      <div className="summary-row">
                        <span>Shipping:</span>
                        <span>{convertPrice(order.shipping || order.shippingCost || 0)}</span>
                      </div>
                      <div className="summary-row">
                        <span>Tax:</span>
                        <span>{convertPrice(order.tax || 0)}</span>
                      </div>
                      {order.codFee && (
                        <div className="summary-row">
                          <span>COD Fee:</span>
                          <span>{convertPrice(order.codFee)}</span>
                        </div>
                      )}
                      <div className="summary-row summary-total">
                        <span>Total:</span>
                        <span>{convertPrice(order.total || order.amount || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {(order.deliveredAt || order.deliveryDate) && (
                  <div className="delivery-info">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#166534">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Delivered on {formatDate(order.deliveredAt || order.deliveryDate)}
                  </div>
                )}

                <div className="order-actions">
                  <button 
                    className="action-button track"
                    onClick={() => handleTrackOrder(order.id || order._id)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Track Order
                  </button>
                  
                  {(order.status === 'pending' || order.status === 'confirmed') && (
                    <button 
                      className="action-button cancel"
                      onClick={() => handleCancelOrder(order.id || order._id)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      Cancel Order
                    </button>
                  )}
                  
                  <button 
                    className="action-button"
                    onClick={() => setSelectedOrder(order)}
                    style={{ 
                      background: '#8b5cf6', 
                      color: 'white',
                      marginLeft: 'auto'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;